import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import * as Sentry from "@sentry/react"; // For error tracking
import { db } from "../config/firebase";

// Define the Profile interface
export interface Profile {
  id?: string; // Profile ID (optional for creation)
  userId?: string; // Reference to the user who owns this profile (optional for creation)
  type: "customer" | "expert"; // Profile type
  firstName?: string; // Public first name
  name?: string; // Alternative name field
  lastName?: string; // Public last name (made optional)
  email?: string; // Public email (made optional)
  organizationName?: string; // Organization name (made optional)
  teamName?: string; // Team name (made optional)
  teamRole?: string; // Team role (e.g., "team_lead") (made optional)
  teamId?: string; // Team ID
  rating?: number; // Average rating (made optional)
  reviewCount?: number; // Number of reviews (made optional)
  createdAt?: Timestamp; // Timestamp when the profile was created (made optional)
  updatedAt?: Timestamp; // Timestamp when the profile was last updated (made optional)

  // Expert-specific fields (optional for customer profiles)
  availability?: boolean; // Availability schedule (e.g., "9am-5pm")
  ratePerMinute?: number; // Rate per minute for expert services (changed from string to number)
  specialities?: string[]; // List of specialities (e.g., ["AI", "Data Science"])
  bio?: string; // Expert bio
  otherSpecify?: string; // Other specialties specified
}

/**
 * Creates a new profile for a user.
 * @param userId The user ID
 * @param profileData The profile data
 */
export const createProfile = async (
  userId: string,
  profileData: Partial<Profile>
) => {
  try {
    const profilesRef = collection(db, "profiles");
    const profileDoc = doc(profilesRef);

    const newProfile = {
      id: profileDoc.id,
      userId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      ...profileData,
    };

    await setDoc(profileDoc, newProfile);

    return { success: true, profile: newProfile };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { operation: "createProfile" },
      extra: { userId, profileData },
    });
    console.error("Error creating profile:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Updates an existing profile by userId.
 * @param userId The userId of the profile to update
 * @param updates The fields to update
 */
export const updateProfile = async (
  userId: string,
  updates: Partial<Profile>
) => {
  try {
    const profilesRef = collection(db, "profiles");
    const q = query(profilesRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error("Profile not found for userId: " + userId);
    }
    const profileDoc = querySnapshot.docs[0];
    // Clean updates: ensure specialities is array, ratePerMinute is number, remove undefined/null
    const cleanUpdates: Partial<Profile> = { ...updates };
    if (cleanUpdates.specialities !== undefined) {
      cleanUpdates.specialities = Array.isArray(cleanUpdates.specialities)
        ? cleanUpdates.specialities
        : [];
    }
    if (cleanUpdates.ratePerMinute !== undefined) {
      cleanUpdates.ratePerMinute = Number(cleanUpdates.ratePerMinute) || 0;
    }
    Object.keys(cleanUpdates).forEach((key) => {
      const typedKey = key as keyof Profile;
      if (
        cleanUpdates[typedKey] === undefined ||
        cleanUpdates[typedKey] === null
      ) {
        delete cleanUpdates[typedKey];
      }
    });
    await updateDoc(profileDoc.ref, {
      ...cleanUpdates,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error) {
    Sentry.captureException(error, {
      tags: { operation: "updateProfile" },
      extra: { userId, updates },
    });
    console.error("Error updating profile:", error);
    return { success: false, error: (error as Error).message };
  }
};

/**
 * Fetches a user's expert profile.
 * @param userId The user ID
 */
// Get expert profile from profiles collection
export const getExpertProfile = async (userId: string) => {
  try {
    const profilesRef = collection(db, "profiles");
    const q = query(profilesRef, where("userId", "==", userId));
    const profileSnapshot = await getDocs(q);

    if (!profileSnapshot.empty) {
      const profileDoc = profileSnapshot.docs[0];
      return {
        success: true,
        profile: {
          id: profileDoc.id,
          ...profileDoc.data(),
        },
      };
    } else {
      // Fallback to users collection
      const userDocRef = doc(db, `users/${userId}`);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        return {
          success: true,
          profile: {
            id: userDoc.id,
            ...userDoc.data(),
          },
        };
      }
    }

    return {
      success: false,
      error: "Expert profile not found",
    };
  } catch (error: any) {
    console.error("Error fetching expert profile:", error);
    return {
      success: false,
      error: error?.message || "Failed to fetch expert profile",
    };
  }
};

/**
 * Fetches a user's customer profile.
 * @param userId The user ID
 */
// Get customer profile from profiles collection
export const getCustomerProfile = async (userId: string) => {
  try {
    const profilesRef = collection(db, "profiles");
    const q = query(profilesRef, where("userId", "==", userId));
    const profileSnapshot = await getDocs(q);

    if (!profileSnapshot.empty) {
      const profileDoc = profileSnapshot.docs[0];
      return {
        success: true,
        profile: {
          id: profileDoc.id,
          ...profileDoc.data(),
        },
      };
    } else {
      // Fallback to users collection
      const userDocRef = doc(db, `users/${userId}`);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        return {
          success: true,
          profile: {
            id: userDoc.id,
            ...userDoc.data(),
          },
        };
      }
    }

    return {
      success: false,
      error: "Customer profile not found",
    };
  } catch (error: any) {
    console.error("Error fetching customer profile:", error);
    return {
      success: false,
      error: error?.message || "Failed to fetch customer profile",
    };
  }
};

/**
 * Subscribes to real-time updates for a profile.
 * @param profileId The ID of the profile to subscribe to
 * @param onUpdate Callback function to handle profile updates
 */
// Subscribe to profile updates
export const subscribeToProfile = (
  profileId: string,
  callback: (profile: any) => void
) => {
  const profilesRef = collection(db, "profiles");
  const q = query(profilesRef, where("userId", "==", profileId));

  // This would typically return an unsubscribe function
  // Implementation depends on your specific real-time setup
  return () => {
    // Cleanup logic
  };
};
